import { useMemo, useState } from 'react';
import { eventLogsColumns } from '../../../tableColumns/hardwareColumns';
import { IHardwareEvent } from '../../../ts/interface/hardware.interface';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import CardView from '../../../components/Redesing/card-view';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import { DEFAULT_ROWS_PER_PAGE, EVENT_LOGS_TYPES } from '../../../constants';
import { SeverityPillColor } from '../../../ts/types/settings';
import useUrlState from '../../../hooks/use-url-state';
import { Option, SearchChip } from '../../../ts/interface/components/table.interface';
import {
  MovaticFieldGroupDate,
  MovaticFieldGroupSelectFilter,
} from '../../../components/Redesing/movatic-field-group';
import {
  useHardwareEventsRelayQuery,
  EventSortInput,
  EventFilterInput,
} from '../../../hooks/hardware/query/V2/useHardwareEventsRelay';

const SORT_KEY_MAP: Record<string, string> = {
  created_on: 'CREATED_ON',
  recorded_at: 'RECORDED_AT',
  type: 'TYPE',
};

// Options for the Type filter. "Important" is a meta-type that expands to all
// the notable event types (same behavior as the rental event-logs view).
const eventTypeOptions: Option[] = EVENT_LOGS_TYPES.map((t) => ({
  label: t.name,
  value: t.value,
}));

const EventLogs = ({
  hardware_id,
  isLoadingHardware,
}: {
  hardware_id: string;
  isLoadingHardware: boolean;
}) => {
  const [exportData, setExportData] = useState<boolean>(false);
  const [events, setEvents] = useState<IHardwareEvent[]>([]);

  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(0);

  const [filters, setFilters, isComplete, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    search: '',
    after_created_on: '',
    before_created_on: '',
    types: '',
    sortConfig: {
      key: 'recorded_at',
      direction: 'desc',
    } as ISortConfig,
  });

  const [chips, setChips] = useState<SearchChip[]>([]);

  const [selectedAfterCreatedOn, setSelectedAfterCreatedOn] = useState<Date | null>(null);
  const [selectedbeforeCreatedOn, setSelectedbeforeCreatedOn] = useState<Date | null>(
    null
  );
  // Pending Type selection shown in the modal until "Apply".
  const [selectedTypes, setSelectedTypes] = useState<Option[]>([]);

  // Sync applied Type chips + the pending selection from the (URL-persisted)
  // applied `types` value, so the chip and query stay in lock-step on refresh.
  const syncTypeChips = (values: string[]) => {
    setChips((prev) => {
      const withoutTypes = prev.filter((chip) => chip.field !== 'types');
      const typeChips: SearchChip[] = values.map((value) => ({
        label: 'Type',
        field: 'types',
        value,
        displayValue: eventTypeOptions.find((o) => o.value === value)?.label ?? value,
      }));
      return [...withoutTypes, ...typeChips];
    });
  };

  const sort = useMemo<EventSortInput[]>(() => {
    const field = SORT_KEY_MAP[filters.sortConfig.key] || 'RECORDED_AT';
    const order = filters.sortConfig.direction === 'asc' ? 'ASC' : 'DESC';
    return [{ field, order } as EventSortInput];
  }, [filters.sortConfig]);

  const filter = useMemo<EventFilterInput | undefined>(() => {
    const conditions: EventFilterInput[] = [];

    // The date pickers bound recordedAt — when the event happened — even
    // though the URL state keys keep their legacy *_created_on names.
    if (filters.after_created_on) {
      conditions.push({
        recordedAt: { greaterThanOrEqual: filters.after_created_on },
      });
    }
    if (filters.before_created_on) {
      conditions.push({
        recordedAt: { lessThanOrEqual: filters.before_created_on },
      });
    }
    if (filters.search) {
      conditions.push({
        type: { ilike: `%${filters.search}%` },
      });
    }
    if (filters.types) {
      const selected = filters.types.split(',').filter(Boolean);
      // "Important" expands to every notable event type.
      const expanded = selected.includes('Important')
        ? EVENT_LOGS_TYPES.filter((t) => t.value !== 'Important').map((t) => t.value)
        : selected;
      if (expanded.length > 0) {
        conditions.push({ type: { in: expanded } });
      }
    }

    if (conditions.length === 0) return undefined;
    if (conditions.length === 1) return conditions[0];
    return { and: conditions };
  }, [
    filters.after_created_on,
    filters.before_created_on,
    filters.search,
    filters.types,
  ]);

  const {
    data: eventsData,
    hasNextPage,
    endCursor,
    isLoading,
  } = useHardwareEventsRelayQuery(hardware_id, {
    enabled: !!hardware_id && isComplete,
    first: filters.sizePerPage,
    after: afterCursor,
    sort,
    filter,
  });

  // Map GraphQL events and accumulate across pages
  useMemo(() => {
    if (!eventsData.length && !afterCursor) {
      setEvents([]);
      return;
    }
    const mapped = eventsData.map((event) => {
      const colorType = EVENT_LOGS_TYPES.find(({ value }) => value === event.type);
      return {
        id: event.id,
        created_on: event.createdOn,
        recorded_at: event.recordedAt,
        type: event.type ?? '',
        color_type: (colorType?.color as SeverityPillColor) ?? null,
      };
    });
    if (afterCursor) {
      setEvents((prev) => [...prev, ...mapped]);
    } else {
      setEvents(mapped);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsData]);

  const handleDateChange = (field: 'startDate' | 'endDate', date: Date | null): void => {
    if (date) {
      setChips((prevChips) => {
        const chip: SearchChip = {
          label: field === 'startDate' ? 'Start Date' : 'End Date',
          field: field,
          value: date,
          displayValue: date.toLocaleDateString(),
        };
        return [...prevChips.filter((chip) => chip.field !== field), chip];
      });
    }
  };

  // Restore date + type chips (and the pending Type selection) from URL state
  // on initial load, so a refresh shows the filters that are actually applied.
  useMemo(() => {
    if (isComplete) {
      if (filters.after_created_on) {
        handleDateChange('startDate', new Date(filters.after_created_on));
        setSelectedAfterCreatedOn(new Date(filters.after_created_on));
      }
      if (filters.before_created_on) {
        handleDateChange('endDate', new Date(filters.before_created_on));
        setSelectedbeforeCreatedOn(new Date(filters.before_created_on));
      }
      if (filters.types) {
        const values = filters.types.split(',').filter(Boolean);
        syncTypeChips(values);
        setSelectedTypes(
          values.map((value) => ({
            label: eventTypeOptions.find((o) => o.value === value)?.label ?? value,
            value,
          }))
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete]);

  const resetPagination = () => {
    setAfterCursor(null);
    setPageNumber(0);
  };

  const handleChipDelete = (deletedGroup: SearchChip) => {
    if (deletedGroup.label === 'Start Date') {
      resetPagination();
      setFilters((prev) => ({
        ...prev,
        after_created_on: '',
      }));
      setChips((prevChips) => prevChips.filter((chip) => chip.field !== 'startDate'));
      setSelectedAfterCreatedOn(null);
    }
    if (deletedGroup.label === 'End Date') {
      resetPagination();
      setFilters((prev) => ({
        ...prev,
        before_created_on: '',
      }));
      setChips((prevChips) => prevChips.filter((chip) => chip.field !== 'endDate'));
      setSelectedbeforeCreatedOn(null);
    }
    if (deletedGroup.label === 'Type') {
      resetPagination();
      setEvents([]);
      setFilters((prev) => ({
        ...prev,
        types: '',
      }));
      setChips((prevChips) => prevChips.filter((chip) => chip.field !== 'types'));
      setSelectedTypes([]);
    }
  };

  return (
    <>
      <CardView
        headerActions={
          <IconButtonMenu
            buttons={[
              {
                label: 'Export',
                onClick: () => setExportData(true),
                startIcon: <FileDownloadIcon />,
              },
            ]}
          />
        }
        content={
          <>
            <PaginationTable
              dataId={'hardware-event-logs'}
              showFilter
              customKey={'id'}
              sortDirection={'desc'}
              exportData={exportData}
              csvFileName={'Hardware_Events_Logs.csv'}
              onExport={() => setExportData(false)}
              sortBy={filters.sortConfig.key}
              chips={chips}
              handleChipDelete={handleChipDelete}
              hasMore={hasNextPage}
              onApplyFilter={() => {
                resetPagination();
                if (selectedAfterCreatedOn && !isNaN(selectedAfterCreatedOn.getTime())) {
                  handleDateChange('startDate', selectedAfterCreatedOn);
                  setFilters((prev) => ({
                    ...prev,
                    after_created_on: selectedAfterCreatedOn.toISOString(),
                  }));
                }
                if (
                  selectedbeforeCreatedOn &&
                  !isNaN(selectedbeforeCreatedOn.getTime())
                ) {
                  handleDateChange('endDate', selectedbeforeCreatedOn);
                  setFilters((prev) => ({
                    ...prev,
                    before_created_on: selectedbeforeCreatedOn.toISOString(),
                  }));
                }
                setEvents([]);
                const typeValues = selectedTypes.map((t) => t.value.toString());
                setFilters((prev) => ({ ...prev, types: typeValues.join(',') }));
                syncTypeChips(typeValues);
              }}
              spaceFilters={
                <>
                  <MovaticFieldGroupSelectFilter
                    dataId="hardware-event-logs-type"
                    label="Type"
                    value={selectedTypes}
                    onChange={(event: React.SyntheticEvent, newValue: any[]) =>
                      setSelectedTypes(newValue)
                    }
                    options={eventTypeOptions}
                  />
                  <MovaticFieldGroupDate
                    id={'hardware-event-logs-start-date'}
                    label="Start Date"
                    value={selectedAfterCreatedOn}
                    onChange={(date) => setSelectedAfterCreatedOn(date)}
                  />
                  <MovaticFieldGroupDate
                    id={'hardware-event-logs-end-date'}
                    label="End Date"
                    value={selectedbeforeCreatedOn}
                    onChange={(date) => setSelectedbeforeCreatedOn(date)}
                  />
                </>
              }
              onSortChange={(sort) => {
                setEvents([]);
                resetPagination();
                setFilters({
                  ...filters,
                  sortConfig: {
                    key: sort.key,
                    direction: sort.direction as 'desc' | 'asc',
                  },
                });
              }}
              handleSearch={(event) => {
                setEvents([]);
                resetPagination();
                setFilters({
                  ...filters,
                  search: event.target.value,
                });
              }}
              searchText={filters.search}
              isLoading={
                isLoading ||
                !hardware_id ||
                isLoadingHardware ||
                !isComplete ||
                isRestoring
              }
              columns={eventLogsColumns as IColumn[]}
              items={events || []}
              onPageChange={(page, newPage) => {
                const pageNumberAux = newPage + 1;
                if (
                  events?.length &&
                  events.length / filters.sizePerPage < pageNumberAux
                ) {
                  setAfterCursor(endCursor);
                }
                setPageNumber(newPage);
              }}
              onRowsPerPageChange={(pageSizeString) => {
                const pageSize = Number(pageSizeString.target.value);
                setEvents([]);
                resetPagination();
                setFilters({
                  ...filters,
                  sizePerPage: pageSize,
                });
              }}
              page={pageNumber}
              rowsPerPage={filters.sizePerPage}
              searchPlaceholder={'Search Event Logs'}
              noDataText={'No event logs for this unit'}
              localSearch={false}
            />
          </>
        }
        title={'Event Logs'}
      />
    </>
  );
};

export default EventLogs;
